import { useEffect, useRef, useState, useCallback } from 'react';

// types
import type { ListResult, Material, Media, MediaType, SearchResult } from '../types/mynewsdesk.types';
import type {
  ExcutionResult,
  FetchMaterialsResult,
  MaterialsGenerator,
  MaterialsRequestConfig,
  Reducer,
} from '../types/press-portal.types';

// utils
import {
  listGenerator,
  listResultsReducer,
  searchGenerator,
  searchResultsReducer,
} from './helpers';
import { useCurrentLanguage } from 'utils/hooks/useCurrentLanguage';
import { sortByDate } from '../utils';

function useFetchMaterials<T extends Material | Media>({
  mediaTypes = [],
  mediaType,
  search,
  limit,
  locale,
  initialItems,
  info,
}: MaterialsRequestConfig): FetchMaterialsResult<T> {
  const iteration = useRef<number>(initialItems ? 1 : 0);

  let flatInitialResults = { items: [] as Material[], done: false };

  if (initialItems) {
    flatInitialResults = {
      items: initialItems,
      done: info ? Object.values(info).reduce((acc, { done }) => (acc && done), true) : false,
    };
  }

  const getCurrentLimit = () => typeof limit === 'function' ? limit(iteration.current) : limit;

  const [loading, setLoading] = useState<boolean>(false);
  const [loadable, setLoadable] = useState<boolean>(flatInitialResults.done);

  const buffer = useRef(new Set<T>(flatInitialResults.items.slice(getCurrentLimit()) as T[]));

  const [materials, setMaterials] = useState<T[] | null>(() => {
    if (flatInitialResults.items) {
      const paged = [...flatInitialResults.items].splice(0, getCurrentLimit());

      return paged as T[];
    }
    return [];
  });

  const generators = useRef<MaterialsGenerator<ListResult | SearchResult>>([]);
  const [error, setError] = useState<string | null>(null);
  const lang = useCurrentLanguage();
  const mediaTypesString = mediaTypes.join();

  const consumeGenerators = useCallback(async (reducer: Reducer) => {
    const resultsPromises = generators.current.map((generator) => generator.next());
    const results = await Promise.all(resultsPromises);
    const result: ExcutionResult = results.reduce(reducer, { done: true, items: [] });
    const allMaterials = [...result.items, ...Array.from(buffer.current)].sort(sortByDate);
    const newMaterials = allMaterials.splice(0, getCurrentLimit());

    // remove consumed materials from the list, if any
    newMaterials.forEach((m) => {
      if (buffer.current.has(m as T)) {
        buffer.current.delete(m as T);
      }
    });

    // put remaining materials into the buffer for later processing
    allMaterials.forEach((m) => {
      buffer.current?.add(m as T);
    });

    iteration.current += 1;
    setMaterials((mat) => (mat ? [...mat, ...(newMaterials as T[])] : (newMaterials as T[])));
    setLoadable(!result.done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildGenerators = useCallback(
    () => {
      const currentLimit = getCurrentLimit();

      if (search && typeof search === 'string') {
        generators.current = (mediaType ? [mediaType] : mediaTypes).map(
          (type_of_media: MediaType) =>
            searchGenerator({
              limit: currentLimit,
              query: search,
              type_of_media,
              lang,
              pressroom: locale,
            }),
        );
      } else {
        generators.current = (mediaType ? [mediaType] : mediaTypes).map(
          (type_of_media: MediaType) =>
            listGenerator({
              limit: currentLimit,
              type_of_media,
              lang,
              locale: locale?.toLowerCase(),
              pressroom: locale,
              info: (generators.current.length === 0) ? info?.[type_of_media] : undefined,
            }),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, mediaType, mediaTypes, search, lang],
  );

  const handleGenerators = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    setError(null);
    try {
      if (search) {
        await consumeGenerators(searchResultsReducer);
      } else {
        await consumeGenerators(listResultsReducer);
      }
    } catch (err: unknown) {
      const error = err as Error;
      setError(`internal error: ${error.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumeGenerators, search, lang]);

  useEffect(() => {
    if (!mediaType && (!mediaTypes || mediaTypes.length === 0)) return;

    if (generators.current.length === 0 && initialItems) {
      buildGenerators();
    } else {
      buffer.current.clear();
      iteration.current = 0;
      setMaterials(null);
      buildGenerators();
      handleGenerators();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mediaType,
    search,
    lang,
    mediaTypesString,
    locale,
    mediaTypes,
    buildGenerators,
    handleGenerators,
  ]);

  return {
    loading,
    error,
    materials,
    loadMore: handleGenerators,
    loadable: loadable || buffer.current.size > 0,
    total: 0,
  };
}

export { useFetchMaterials };
